import { memo } from 'react';
import styled from 'styled-components';

interface LoadingSpinnerProps {
  size?: LoadingSpinnerSizes;
  color?: string;
  testId?: string;
  full?: boolean;
  className?: string;
}

export enum LoadingSpinnerSizes {
  DEFAULT = '1rem',
  EXTRA_SMALL = '0.875rem',
  SMALL = '1.5rem',
  MEDIUM = '3rem',
  LARGE = '6rem',
}

const StyledSpinner = styled.div<{
  $size?: LoadingSpinnerSizes;
  $color?: string;
  $full?: boolean;
}>`
  min-height: 12.5rem;
  height: ${({ $full }) => ($full ? '100vh' : 'inherit')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;

  .spinner {
    animation: rotate 1.5s linear infinite;
    width: ${({ $size }) => $size};

    circle {
      stroke: ${({ $color }) => $color};
      stroke-width: 3px;
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
      animation: dash 1.4s ease-in-out infinite;
      stroke-linecap: round;
      transition: stroke 1s ease-in-out;
    }
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
    }

    50% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -35px;
    }

    100% {
      stroke-dasharray: 89, 200;
      stroke-dashoffset: -125px;
    }
  }
`;

export const LoadingSpinner = ({
  size = LoadingSpinnerSizes.MEDIUM,
  color = 'blue',
  testId = 'loading-spinner',
  full = false,
  className,
}: LoadingSpinnerProps) => {
  return (
    <StyledSpinner
      role="status"
      data-testid={testId}
      className={className}
      $color={color}
      $size={size}
      $full={full}
    >
      <svg
        className="spinner"
        viewBox="25 25 50 50"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
        role="img"
        aria-label="Loading"
      >
        <circle
          className="inner-circle"
          cx="50"
          cy="50"
          r="20"
          fill="none"
          strokeMiterlimit="10"
          strokeLinecap="butt"
        />
      </svg>
    </StyledSpinner>
  );
};

export default memo(LoadingSpinner);
